import { useQueryClient } from '@tanstack/react-query';
import { Panel, useReactFlow, useStore, useViewport } from '@xyflow/react';
import { ZfIconButton, ZfTextButton, ZfTooltip } from '@zafire/react';
import { type ChangeEvent, type FunctionComponent, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteNodePositions } from '../api/mutations';
import { queryKeys } from '../api/query-keys';

interface ZoomControlsProps {
    setAutoPositionNodes: (shouldAutoPositionNodes: boolean) => void;
    partnerUuid: string;
}

export const ZoomControls: FunctionComponent<ZoomControlsProps> = ({ setAutoPositionNodes, partnerUuid }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { zoom } = useViewport();
    const { zoomTo, zoomIn, zoomOut, fitView } = useReactFlow();
    const actionClickedRef = useRef(false);

    const { mutateAsync: deleteNodePositions } = useDeleteNodePositions(partnerUuid);

    const { minZoom, maxZoom } = useStore(
        (state) => ({
            minZoom: state.minZoom,
            maxZoom: state.maxZoom,
        }),
        (a, b) => a.minZoom !== b.minZoom || a.maxZoom !== b.maxZoom,
    );

    const fillPercent = useMemo(() => ((zoom - minZoom) / (maxZoom - minZoom)) * 100, [maxZoom, minZoom, zoom]);

    const onAutoPositionNodesClick = useCallback(() => {
        setAutoPositionNodes(true);
        document.dispatchEvent(
            new CustomEvent('show-toast', {
                detail: {
                    type: 'success',
                    position: 'bottom-right',
                    description: t('autoPositionReset'),
                    leadingIcon: 'check_circle',
                    actionButtonText: t('autoPositionButtonText'),
                    dismissible: true,
                    toastActionClick: () => {
                        actionClickedRef.current = true;
                        setAutoPositionNodes(false);
                        queryClient.invalidateQueries({ queryKey: queryKeys.familyTree(partnerUuid) });
                    },
                    toastDismiss: async () => {
                        if (actionClickedRef.current) {
                            actionClickedRef.current = false;

                            return;
                        }
                        await deleteNodePositions();
                        setAutoPositionNodes(false);
                    },
                },
            }),
        );
    }, [deleteNodePositions, partnerUuid, queryClient, setAutoPositionNodes, t]);

    return (
        <Panel className="zoom-controls" position="bottom-right">
            <ZfIconButton icon="remove" hierarchy="tertiary" size="small" onClick={() => zoomOut({ duration: 300 })} />
            <input
                className="zoom-slider"
                type="range"
                value={zoom}
                min={minZoom}
                max={maxZoom}
                step={0.01}
                onChange={(e: ChangeEvent<HTMLInputElement>) => zoomTo(parseFloat(e.target.value))}
                style={{
                    background: `linear-gradient(to right, var(--bg-color-action-muted) ${fillPercent}%, var(--bg-color-default-stronger) ${fillPercent}%)`,
                }}
            />
            <ZfIconButton icon="add" hierarchy="tertiary" size="small" onClick={() => zoomIn({ duration: 300 })} />
            <ZfTextButton className="zoom-value-button" hierarchy="tertiary" size="small" onClick={() => zoomTo(1, { duration: 300 })}>
                {(100 * zoom).toFixed(0)}%
            </ZfTextButton>
            <ZfTooltip text={t('autoPositionTooltipText')}>
                <ZfIconButton slot="anchor" icon="vertical_align_center" hierarchy="tertiary" size="medium" onClick={onAutoPositionNodesClick} />
            </ZfTooltip>
            <ZfIconButton icon="fullscreen" hierarchy="tertiary" size="medium" onClick={() => fitView({ duration: 300 })} />
        </Panel>
    );
};
